import { useState, useRef } from 'react'
import {
  Settings,
  Eye,
  Code,
  Bold,
  Italic,
  List,
  Link as LinkIcon,
  Image,
  Quote,
  Heading1,
  Heading2,
  Heading3,
  FileUp,
  FileDown,
  Copy,
  CodeXml,
  Paintbrush,
  Workflow,
  Presentation,
  Brush,
  FileCode2,
  ListOrdered,
  CheckSquare,
  Table,
  Minus,
  Maximize2,
  Minimize2,
} from 'lucide-react'

const defaultMarkdown = `# 欢迎使用 MultiPost Markdown 编辑器

这是一个功能强大的 Markdown 编辑器，支持实时预览和多种格式。

## 功能特性

- **粗体文本** 和 *斜体文本*
- [链接示例](https://multipost.app)
- 列表支持
- 代码高亮
- 数学公式
- Mermaid 图表

## 代码示例

\`\`\`javascript
function hello() {
  console.log("Hello, MultiPost!");
  return "Welcome to MultiPost";
}
\`\`\`

## 数学公式

行内公式：$E = mc^2$

块级公式：
$$
\\sum_{i=1}^{n} i = \\frac{n(n+1)}{2}
$$

## 表格

| 功能 | 支持情况 |
|------|----------|
| 粗体 | ✓ |
| 斜体 | ✓ |
| 链接 | ✓ |
| 图片 | ✓ |
| 代码 | ✓ |
| 表格 | ✓ |

## 引用

> 这是一段引用文本，用于展示引用样式。
>
> — MultiPost

## 任务列表

- [x] 完成首页设计
- [x] 完成仪表盘
- [ ] 完成编辑器
- [ ] 完成发布功能

---

开始编辑你的内容吧！`

export default function MarkdownEditor() {
  const [markdown, setMarkdown] = useState(defaultMarkdown)
  const [showPreview, setShowPreview] = useState(true)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const editorRef = useRef<HTMLTextAreaElement>(null)

  const handleInsertMarkdown = (prefix: string, suffix: string = '') => {
    const textarea = editorRef.current
    if (!textarea) return

    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const selectedText = markdown.substring(start, end)
    const newText = markdown.substring(0, start) + prefix + selectedText + suffix + markdown.substring(end)
    setMarkdown(newText)

    // Restore cursor position
    setTimeout(() => {
      textarea.focus()
      textarea.setSelectionRange(start + prefix.length, start + prefix.length + selectedText.length)
    }, 0)
  }

  const handleBold = () => handleInsertMarkdown('**', '**')
  const handleItalic = () => handleInsertMarkdown('*', '*')
  const handleHeading1 = () => handleInsertMarkdown('# ')
  const handleHeading2 = () => handleInsertMarkdown('## ')
  const handleHeading3 = () => handleInsertMarkdown('### ')
  const handleLink = () => handleInsertMarkdown('[', '](url)')
  const handleImage = () => handleInsertMarkdown('![alt](', ')')
  const handleCode = () => handleInsertMarkdown('`', '`')
  const handleCodeBlock = () => handleInsertMarkdown('\n```\n', '\n```\n')
  const handleQuote = () => handleInsertMarkdown('> ')
  const handleList = () => handleInsertMarkdown('- ')
  const handleOrderedList = () => handleInsertMarkdown('1. ')
  const handleTaskList = () => handleInsertMarkdown('- [ ] ')
  const handleTable = () => handleInsertMarkdown('\n| 列1 | 列2 | 列3 |\n|------|------|------|\n| 内容 | 内容 | 内容 |\n')
  const handleHorizontalRule = () => handleInsertMarkdown('\n---\n')

  const handleCopyWechat = () => {
    navigator.clipboard.writeText(markdown)
    alert('已复制为微信格式')
  }

  const handleCopyZhihu = () => {
    navigator.clipboard.writeText(markdown)
    alert('已复制为知乎格式')
  }

  const handleCopyJuejin = () => {
    navigator.clipboard.writeText(markdown)
    alert('已复制为掘金格式')
  }

  const handleCopyHtml = () => {
    navigator.clipboard.writeText(markdown)
    alert('已复制 HTML')
  }

  const handleExportMarkdown = () => {
    const blob = new Blob([markdown], { type: 'text/markdown' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'document.md'
    a.click()
    URL.revokeObjectURL(url)
  }

  const handleImportFile = () => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = '.md,.txt'
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0]
      if (file) {
        const reader = new FileReader()
        reader.onload = (e) => {
          setMarkdown(e.target?.result as string)
        }
        reader.readAsText(file)
      }
    }
    input.click()
  }

  return (
    <div className={`flex flex-col ${isFullscreen ? 'fixed inset-0 z-50 bg-background' : ''}`}>
      {/* Main Content */}
      <main className="flex-1">
        <div className="flex h-[calc(100vh-4rem)] flex-col">
          {/* Top Toolbar */}
          <div className="flex items-center justify-between border-b border-[hsl(var(--border))] px-4 py-2">
            <div className="flex items-center gap-2">
              <h1 className="text-lg font-semibold text-[hsl(var(--foreground))]">Markdown 排版</h1>
              <span className="rounded-full bg-[hsl(var(--muted))] px-2 py-0.5 text-xs text-[hsl(var(--muted-foreground))]">
                草稿
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button className="inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-sm hover:bg-[hsl(var(--accent))]">
                <Image className="size-3.5" />
                素材库
              </button>
            </div>
          </div>

          {/* Editor Toolbar */}
          <div className="flex items-center gap-1 border-b border-[hsl(var(--border))] px-4 py-1.5">
            {/* Basic Formatting */}
            <button onClick={handleBold} title="粗体" className="rounded p-1.5 hover:bg-[hsl(var(--accent))]">
              <Bold className="size-4" />
            </button>
            <button onClick={handleItalic} title="斜体" className="rounded p-1.5 hover:bg-[hsl(var(--accent))]">
              <Italic className="size-4" />
            </button>
            <button onClick={handleHeading1} title="标题1" className="rounded p-1.5 hover:bg-[hsl(var(--accent))]">
              <Heading1 className="size-4" />
            </button>
            <button onClick={handleHeading2} title="标题2" className="rounded p-1.5 hover:bg-[hsl(var(--accent))]">
              <Heading2 className="size-4" />
            </button>
            <button onClick={handleHeading3} title="标题3" className="rounded p-1.5 hover:bg-[hsl(var(--accent))]">
              <Heading3 className="size-4" />
            </button>

            <div className="mx-1 h-4 w-px bg-[hsl(var(--border))]" />

            {/* Lists */}
            <button onClick={handleList} title="无序列表" className="rounded p-1.5 hover:bg-[hsl(var(--accent))]">
              <List className="size-4" />
            </button>
            <button onClick={handleOrderedList} title="有序列表" className="rounded p-1.5 hover:bg-[hsl(var(--accent))]">
              <ListOrdered className="size-4" />
            </button>
            <button onClick={handleTaskList} title="任务列表" className="rounded p-1.5 hover:bg-[hsl(var(--accent))]">
              <CheckSquare className="size-4" />
            </button>

            <div className="mx-1 h-4 w-px bg-[hsl(var(--border))]" />

            {/* Insert */}
            <button onClick={handleLink} title="链接" className="rounded p-1.5 hover:bg-[hsl(var(--accent))]">
              <LinkIcon className="size-4" />
            </button>
            <button onClick={handleImage} title="图片" className="rounded p-1.5 hover:bg-[hsl(var(--accent))]">
              <Image className="size-4" />
            </button>
            <button onClick={handleCode} title="行内代码" className="rounded p-1.5 hover:bg-[hsl(var(--accent))]">
              <Code className="size-4" />
            </button>
            <button onClick={handleCodeBlock} title="代码块" className="rounded p-1.5 hover:bg-[hsl(var(--accent))]">
              <FileCode2 className="size-4" />
            </button>
            <button onClick={handleQuote} title="引用" className="rounded p-1.5 hover:bg-[hsl(var(--accent))]">
              <Quote className="size-4" />
            </button>
            <button onClick={handleTable} title="表格" className="rounded p-1.5 hover:bg-[hsl(var(--accent))]">
              <Table className="size-4" />
            </button>
            <button onClick={handleHorizontalRule} title="分割线" className="rounded p-1.5 hover:bg-[hsl(var(--accent))]">
              <Minus className="size-4" />
            </button>

            <div className="flex-1" />

            {/* Right Actions */}
            <button onClick={handleImportFile} title="导入文件" className="rounded p-1.5 hover:bg-[hsl(var(--accent))]">
              <FileUp className="size-4" />
            </button>
            <button onClick={handleExportMarkdown} title="导出 Markdown" className="rounded p-1.5 hover:bg-[hsl(var(--accent))]">
              <FileDown className="size-4" />
            </button>
            <button title="编辑器设置" className="rounded p-1.5 hover:bg-[hsl(var(--accent))]">
              <Settings className="size-4" />
            </button>

            <div className="mx-1 h-4 w-px bg-[hsl(var(--border))]" />

            {/* Copy Actions */}
            <button onClick={handleCopyWechat} title="复制为微信格式" className="rounded p-1.5 hover:bg-[hsl(var(--accent))]">
              <Copy className="size-4" />
            </button>
            <button onClick={handleCopyZhihu} title="复制为知乎格式" className="rounded p-1.5 hover:bg-[hsl(var(--accent))]">
              <Copy className="size-4" />
            </button>
            <button onClick={handleCopyJuejin} title="复制为掘金格式" className="rounded p-1.5 hover:bg-[hsl(var(--accent))]">
              <Copy className="size-4" />
            </button>
            <button onClick={handleCopyHtml} title="复制 HTML" className="rounded p-1.5 hover:bg-[hsl(var(--accent))]">
              <CodeXml className="size-4" />
            </button>

            <div className="mx-1 h-4 w-px bg-[hsl(var(--border))]" />

            {/* Style Settings */}
            <button title="排版样式" className="rounded p-1.5 hover:bg-[hsl(var(--accent))]">
              <Paintbrush className="size-4" />
            </button>
            <button title="代码主题" className="rounded p-1.5 hover:bg-[hsl(var(--accent))]">
              <Code className="size-4" />
            </button>
            <button title="流程图主题" className="rounded p-1.5 hover:bg-[hsl(var(--accent))]">
              <Workflow className="size-4" />
            </button>
            <button title="信息图设置" className="rounded p-1.5 hover:bg-[hsl(var(--accent))]">
              <Presentation className="size-4" />
            </button>
            <button title="自定义 CSS" className="rounded p-1.5 hover:bg-[hsl(var(--accent))]">
              <Brush className="size-4" />
            </button>

            <div className="mx-1 h-4 w-px bg-[hsl(var(--border))]" />

            {/* View Controls */}
            <button onClick={() => setIsFullscreen(!isFullscreen)} title={isFullscreen ? '退出全屏' : '全屏'} className="rounded p-1.5 hover:bg-[hsl(var(--accent))]">
              {isFullscreen ? <Minimize2 className="size-4" /> : <Maximize2 className="size-4" />}
            </button>
          </div>

          {/* Editor Area */}
          <div className="flex flex-1 overflow-hidden">
            {/* Editor */}
            <div className={`flex-1 ${showPreview ? 'w-1/2' : 'w-full'}`}>
              <textarea
                ref={editorRef}
                value={markdown}
                onChange={(e) => setMarkdown(e.target.value)}
                className="h-full w-full resize-none bg-[hsl(var(--background))] p-4 font-mono text-sm leading-relaxed focus:outline-none"
                placeholder="输入 Markdown 内容..."
              />
            </div>

            {/* Divider */}
            {showPreview && (
              <div className="w-px bg-[hsl(var(--border))]" />
            )}

            {/* Preview */}
            {showPreview && (
              <div className="w-1/2 overflow-y-auto bg-[hsl(var(--card))]">
                <div className="p-6">
                  <div className="prose prose-sm prose-invert max-w-none">
                    <div dangerouslySetInnerHTML={{ __html: renderMarkdown(markdown) }} />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Bottom Status Bar */}
          <div className="flex items-center justify-between border-t border-[hsl(var(--border))] px-4 py-1.5 text-xs text-[hsl(var(--muted-foreground))]">
            <div className="flex items-center gap-4">
              <span>字数: {markdown.length}</span>
              <span>行数: {markdown.split('\n').length}</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowPreview(!showPreview)}
                className="inline-flex items-center gap-1 rounded px-2 py-1 hover:bg-[hsl(var(--accent))]"
              >
                <Eye className="size-3" />
                {showPreview ? '隐藏预览' : '显示预览'}
              </button>
              <span>Saved</span>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}

// Simple markdown renderer (basic implementation)
function renderMarkdown(md: string): string {
  let html = md
    // Headers
    .replace(/^### (.*$)/gim, '<h3>$1</h3>')
    .replace(/^## (.*$)/gim, '<h2>$1</h2>')
    .replace(/^# (.*$)/gim, '<h1>$1</h1>')
    // Bold
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    // Italic
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    // Code blocks
    .replace(/```(\w+)?\n([\s\S]*?)```/g, '<pre><code class="language-$1">$2</code></pre>')
    // Inline code
    .replace(/`(.*?)`/g, '<code>$1</code>')
    // Links
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
    // Images
    .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" />')
    // Blockquotes
    .replace(/^> (.*$)/gim, '<blockquote>$1</blockquote>')
    // Unordered lists
    .replace(/^\s*- (.*$)/gim, '<li>$1</li>')
    // Ordered lists
    .replace(/^\s*\d+\. (.*$)/gim, '<li>$1</li>')
    // Horizontal rules
    .replace(/^---$/gim, '<hr />')
    // Line breaks
    .replace(/\n/g, '<br />')

  return html
}
